import { Timeout } from '@crossbeam/types';

import { onBeforeUnmount } from 'vue';

type PollerInterval = number | ((calls: number) => number);
type PollerArgs = {
  poller: (calls?: number) => Promise<unknown>;
  interval?: PollerInterval;
  shouldComplete?: (
    pollerResponse?: unknown,
    calls?: number,
  ) => Promise<boolean> | boolean;
  onError?: (e: unknown) => void;
  onComplete?: () => void;
  skipUnmount?: boolean;
  immediate?: boolean;
};

type TimeoutOrNull = Timeout | null;
type PollerWithCancel = { start: () => Timeout; cancel: () => void };

const usePolling = ({
  poller,
  interval = 10000,
  shouldComplete,
  onError,
  onComplete,
  skipUnmount = false,
  immediate = false,
}: PollerArgs): PollerWithCancel => {
  let timeout: TimeoutOrNull;

  function clearAndReset(t: TimeoutOrNull) {
    if (t) clearTimeout(t);
    return null;
  }

  if (!skipUnmount) {
    onBeforeUnmount(() => {
      if (timeout) clearTimeout(timeout);
      timeout = null;
    });
  }

  let calls = 0;
  async function timeoutCallback() {
    calls += 1;
    try {
      const response = await poller(calls);
      const complete = !shouldComplete
        ? false
        : await shouldComplete(response, calls);
      timeout = clearAndReset(timeout);
      const newInterval =
        typeof interval === 'function' ? interval(calls) : interval;
      if (!complete) timeout = setTimeout(timeoutCallback, newInterval);
      else if (onComplete) onComplete();
    } catch (err) {
      timeout = clearAndReset(timeout);
      if (onError) {
        onError(err);
      }
    }
  }

  const firstInterval =
    typeof interval === 'function' ? interval(calls) : interval;
  return {
    start() {
      timeout = setTimeout(timeoutCallback, immediate ? 0 : firstInterval);
      return timeout;
    },
    cancel() {
      if (timeout) {
        clearTimeout(timeout);
        timeout = null;
      }
    },
  };
};

export default usePolling;
